/* eslint-disable @typescript-eslint/no-unused-vars */
import { StoreSet, StoreGet } from 'project-config/types'
import { composeMiddleware } from 'project-config/utils'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import { create } from 'zustand'
import { IOutputState, IOutputStore, IGetListFilesByShareLinksParams } from './type'
import { OutputAPI } from './service'
import { getShareKey } from '@/utils'
import { IFilePanelData } from 'file-panel'
import { HttpCode } from 'project-config/services/request/http-code'
import { useModalStore } from '@/store/modal/modal-store'
import { TipsModal } from '@/components/modal/tips-modal'
import { getTranslateNsT } from '@/utils/get-translate-nst'

const { showGlobalModal } = useModalStore.getState()

const initialState = (): IOutputState => ({
  fileShareInfo: {
    shareUserId: 0,
    shareUserName: '',
    taskIds: [],
    createTime: 0,
    validityTime: 0,
    virtualId: 0,
    raySyncKey: null,
    platform: 0,
    zone: 0,
  },
  originFileList: [], // 原始文件列表，用于过滤
  fileList: [], // 当前文件列表，用于显示
  isLoading: false,
  selectedFiles: [],
  deletedTaskIds: [],
  isTipsed: false,
  // 新增状态字段的初始值
  currentFileTypeFilter: [],
  currentSearchText: '',
  pageNum: 1,
  pageSize: 50,
  lastPageDataLength: 50,
  isLoadMoreLoading: false,
})

const actions = (set: StoreSet<IOutputStore>, get: StoreGet<IOutputStore>) => ({
  getFileShareInfo: async () => {
    const params = {
      shareKey: getShareKey(),
    }
    const { data } = await OutputAPI.getFileShareInfo(params)
    const channel = 2 // web端的channel为2
    const raySyncKey = data.raySyncKey
      ? `${data.raySyncKey}|${channel ?? ''}|${data.platform ?? ''}`
      : ''
    set({
      fileShareInfo: {
        ...data,
        raySyncKey,
      },
    })
  },
  getListFilesByShareLinks: async (params: IGetListFilesByShareLinksParams) => {
    const commonT = getTranslateNsT('common')
    const { originFileList } = get()
    const currentPageNum = Number(params.pageNum ?? 1)
    const currentPageSize = Number(params.pageSize ?? 50)
    const isLoadMore = currentPageNum > 1
    const _params = {
      shareKey: getShareKey(),
      ...params,
    }
    if (!isLoadMore) {
      set({ isLoading: true })
    }
    const { data, code } = await OutputAPI.getListFilesByShareLinks(_params)
    if (!isLoadMore) {
      set({ isLoading: false })
    }
    if (code === HttpCode.NotAllowed) {
      showGlobalModal({
        type: 'blank',
        render: <TipsModal tips={commonT('not_allowed')} />,
      })
      return
    } else if (code === HttpCode.FileShareExpired) {
      showGlobalModal({
        type: 'blank',
        render: <TipsModal tips={commonT('file_share_expired')} />,
      })
      return
    } else if (code === HttpCode.FileShareNotFound) {
      showGlobalModal({
        type: 'blank',
        render: <TipsModal tips={commonT('file_share_not_found')} />,
      })
      return
    }

    if (Array.isArray(data)) {
      const _originFileList = isLoadMore ? originFileList.concat(data) : data
      set({
        originFileList: _originFileList,
        fileList: _originFileList,
        deletedTaskIds: [],
        pageNum: currentPageNum,
        pageSize: currentPageSize,
        lastPageDataLength: data.length,
        ...(isLoadMore ? {} : { currentFileTypeFilter: [], currentSearchText: '' }),
      })
    } else if (typeof data === 'object' && data !== null) {
      const dataList = data.list || []
      const _originFileList = isLoadMore ? originFileList.concat(dataList) : dataList
      set({
        originFileList: _originFileList,
        fileList: _originFileList,
        deletedTaskIds: data.taskIds || [],
        pageNum: currentPageNum,
        pageSize: currentPageSize,
        lastPageDataLength: dataList.length,
        ...(isLoadMore ? {} : { currentFileTypeFilter: [], currentSearchText: '' }),
      })
    }
    if (isLoadMore) {
      set({ isLoadMoreLoading: false })
    }
  },
  loadMoreFilesByShareLinks: () => {
    const { isLoading, isLoadMoreLoading, pageNum, pageSize, lastPageDataLength } = get()
    if (isLoading || isLoadMoreLoading || lastPageDataLength < pageSize) return Promise.resolve()
    const location = new URL(window.location.href)
    const path = location.searchParams.get('path')
    set({ isLoadMoreLoading: true })
    return get().getListFilesByShareLinks({
      treePath: path || '/',
      pageNum: Number(pageNum) + 1,
      pageSize,
    })
  },
  chooseFile: (file: IFilePanelData[]) => {
    set({ selectedFiles: file })
  },
  changeLoading: (loading: boolean) => {
    set({ isLoading: loading })
  },
  setIsTipsed: () => {
    set({ isTipsed: true })
  },
  filterFileData: (fileSuffix: string[]) => {
    set({ currentFileTypeFilter: fileSuffix })
    get()._applyFilters()
  },
  searchFileData: (text: string) => {
    set({ currentSearchText: text })
    get()._applyFilters()
  },
  // 新增辅助函数：根据当前筛选和搜索条件过滤文件列表
  _applyFilters: () => {
    const { originFileList, currentFileTypeFilter, currentSearchText } = get()
    let filteredList = [...originFileList]
    // 应用文件类型筛选
    if (currentFileTypeFilter.length > 0) {
      filteredList = filteredList.filter((item) =>
        currentFileTypeFilter.includes(item.fileType ?? ''),
      )
    }

    // 应用搜索筛选
    if (currentSearchText.trim()) {
      filteredList = filteredList.filter(
        (item) => item.fileName && item.fileName.includes(currentSearchText),
      )
    }

    set({ fileList: filteredList })
  },
})

const getters = (get: StoreGet<IOutputState>) => ({
  currentPath: () => {
    const url = new URL(window.location.href)
    const queryParams = url.searchParams
    return queryParams.get('path') || '/'
  },
  getPlatform: () => {
    return get().fileShareInfo?.platform
  },
  getFileTypeListOptions: () => {
    const fileTypeList = get().fileList.map((item) => item.fileType)
    const uniqueFileTypes = [...new Set(fileTypeList)]
    if (uniqueFileTypes.length === 1 && uniqueFileTypes[0] === undefined) {
      return Promise.resolve([])
    }
    return Promise.resolve(uniqueFileTypes)
  },
})

export const useOutputStore = create<IOutputStore>()(
  subscribeWithSelector(
    composeMiddleware([devtools], (set: StoreSet<IOutputStore>, get: StoreGet<IOutputStore>) => ({
      ...initialState(),
      ...actions(set, get),
      ...getters(get),
    })),
  ),
)
